import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import "./ChatView.css";
import { useLocation } from "react-router-dom";

function ChatView() {
  const [message, setMessage] = useState("");
  const [userName, setUserName] = useState("");
  const [userId, setUserId] = useState("");
  const [messages, setMessages] = useState([]);
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [decodedToken, setDecodedToken] = useState(null);
  const location = useLocation();
  const chat = location?.state?.chat || null;
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    if (storedToken) {
      setToken(storedToken);
      try {
        const decoded = jwtDecode(storedToken);
        setDecodedToken(decoded);
        // console.log("Decoded token:", decoded);
      } catch (error) {
        console.error("Error decoding token:", error);
        setError("Invalid token");
      }
    }
  }, []);

  useEffect(() => {
    if (token && chat?.id) {
      fetchChats();

      const interval = setInterval(() => {
        fetchChats();
      }, 15000);

      return () => clearInterval(interval);
    }
  }, [token, chat?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchChats = async () => {
    setLoading(true);
    setError(null);

    if (!chat?.id) {
      setError("Invalid chat ID");
      setLoading(false);
      return;
    }

    try {
      let response
      if (decodedToken.rolename === "Admin") {
        response = await axios.get(
          `${process.env.REACT_APP_INSTITUTION_BASEURL}/get-chat-by-id/${chat?.id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      } else if (decodedToken.rolename === "Tutor") {
        response = await axios.get(
          `${process.env.REACT_APP_INSTITUTION_BASEURL}/get-chat-by-id-tutor/${chat?.id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
      }
      // const response = await axios.get(
      //   `${process.env.REACT_APP_INSTITUTION_BASEURL}/get-chat-by-id/${chat?.id}`,
      //   {
      //     headers: { Authorization: `Bearer ${token}` },
      //   }
      // );

      // console.log("Response received:", response?.data?.data[0]?.chat);
      setUserName(response?.data?.data[0]?.userDetails?.user_name);
      setUserId(response?.data?.data[0]?.user_id);

      const chatData = response?.data?.data[0]?.chat || [];
      const today = new Date();

      const formattedMessages = chatData.map((msg) => {
        const messageDate = new Date(msg.timestamp);

        const messageDateString = messageDate
          .toLocaleDateString("en-US", {
            month: "short",
            day: "2-digit",
            year: "numeric",
          })
          .replace(",", "");

        const isToday =
          messageDate.getFullYear() === today.getFullYear() &&
          messageDate.getMonth() === today.getMonth() &&
          messageDate.getDate() === today.getDate();

        return {
          id: msg?._id,
          text: msg?.message,
          sender: msg?.sender === "Admin" || msg?.sender === "Tutor" ? "me" : "them",
          time: messageDate?.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          }),
          date: isToday ? "Today" : messageDateString,
        };
      });

      setMessages(formattedMessages);
      // console.log("Formatted messages: ", formattedMessages);
    } catch (error) {
      console.error("Failed to fetch chats:", error);
      setError("Failed to fetch chats.");
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    if (!userId) {
      console.error("User ID is missing, cannot send message.");
      return;
    }

    try {
      const now = new Date();
      const time = now.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });

      const newMessage = {
        id: Date.now(),
        text: message,
        sender: "me",
        time: time,
        date: "Today",
      };

      setMessages([...messages, newMessage]);
      setMessage("");
      let response
      if (decodedToken.rolename === "Admin") {
        response = await axios.post(
          `${process.env.REACT_APP_INSTITUTION_BASEURL}/send-message`,
        {
          user_id: userId,
          messageContent: message,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
        );
      } else if (decodedToken.rolename === "Tutor") {
        response = await axios.post(
          `${process.env.REACT_APP_INSTITUTION_BASEURL}/send-message-tutor`,
        {
          user_id: userId,
          messageContent: message,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
        );
      }

      // const response = await axios.post(
      //   `${process.env.REACT_APP_INSTITUTION_BASEURL}/send-message`,
      //   {
      //     user_id: userId,
      //     messageContent: message,
      //   },
      //   {
      //     headers: { Authorization: `Bearer ${token}` },
      //   }
      // );

      if (response.data.status === "success") {
        fetchChats();
        // console.log("Message sent successfully:", response.data.data);
      } else {
        console.error("Message send failed:", response.data.message);
      }
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  return (
    <div className="chat-view">
      <div className="chat-header">
        <div className="chat-header-content">
          <div className="profile-image">
            <div className="avatar">
              {chat?.imgg ? (
                <img src={chat?.imgg} alt="Profile" />
              ) : (
                <span className="avatar-placeholder">
                  <h4>{chat?.name[0]}</h4>
                </span>
              )}
            </div>
          </div>
          <div className="chat-info">
            <h4 className="user-name">{userName?.toUpperCase()}</h4>
          </div>
        </div>
      </div>

      <div className="messages-container">
        {loading && <p>Loading chats...</p>}
        {/* {error && <p className="error">{error}</p>} */}
        {!loading && !error && messages.length === 0 && (
          <p>No messages found.</p>
        )}

        {!loading && messages.length > 0 && (
          <>
            {messages.map((msg, index) => {
              const prevMsg = messages[index - 1];
              const showDate = !prevMsg || prevMsg?.date !== msg?.date;

              return (
                <React.Fragment key={msg?.id}>
                  {showDate && <div className="timestamp">{msg?.date}</div>}
                  <div className={`message ${msg?.sender}`}>
                    <div className="message-content">{msg?.text}</div>
                    <div className="message-time">{msg?.time}</div>
                  </div>
                </React.Fragment>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      <div className="message-input">
        <form onSubmit={handleSend} className="frmwdt">
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type a message..."
          />
          <button type="submit">
            <FontAwesomeIcon icon={faPaperPlane} />
          </button>
        </form>
      </div>
    </div>
  );
}

export default ChatView;
